/**
 * W14 (C-352 §1) — the five fields the first frame needs, and nothing else.
 *
 * WHY THIS EXISTS, with the number that justifies it. Teacher Home took ~10 s to its first frame
 * because every shell waited on `GET /api/me` before painting anything. SPEED measured the fix on a
 * throttled phone (390x844, CPU 4x, RTT 140, medians of 10): painting from a stored snapshot takes
 * the first frame from 1234 ms to 779.5 ms, with 0 of 10 runs at or over 1 s. Desktop 666 -> 247 ms.
 *
 * ⚑ THE FIELD LIST IS THE WHOLE POINT, AND IT IS THE OWNER'S RULING, NOT A CONVENIENCE. The owner
 * chose option (a) — `id`, `name`, `role`, `tenant_role`, `language` — over storing the full
 * `/api/me` body, which also carries org identity, flags and computed grants. SPEED's measurement
 * says the full body is not needed to get under 1 s, so storing it would be a privacy cost paid for
 * nothing. `SNAPSHOT_FIELDS` is therefore an ALLOW-LIST and it is applied on the way OUT as well as
 * on the way in: a build that once wrote more than five fields cannot make this one read them.
 *
 * ⚑ THIS IS A PAINT, NEVER AN AUTHORIZATION. The snapshot decides what renders in the first frame;
 * it decides nothing a user may DO. `/api/me` is still fetched on every load and replaces it, and
 * every request is still authorized server-side from the token, per request. Which is also why the
 * snapshot is CLEARED rather than updated whenever identity could have changed — see the four call
 * sites in `useAuth` and `WorkspaceSwitchItem`. A stale role that outlives a demotion would be a
 * scope defect wearing a performance win's clothes.
 *
 * `localStorage` rather than `sessionStorage`, unlike the session notice next door: the win is on a
 * COLD load in a new tab, which is exactly what `sessionStorage` would not survive. Every accessor
 * is wrapped — storage throws in a private window with site data blocked, and a first paint is
 * never worth an exception.
 */
const SNAPSHOT_KEY = "cognivio_user_snapshot";

/** The owner-ruled five. Nothing is stored or returned outside this list. */
export const SNAPSHOT_FIELDS = Object.freeze(["id", "name", "role", "tenant_role", "language"]);

/** Project `user` onto the allow-list, dropping keys with no value. */
function project(user) {
  if (!user || typeof user !== "object") return null;
  const out = {};
  for (const field of SNAPSHOT_FIELDS) {
    const value = user[field];
    if (value !== undefined && value !== null) out[field] = value;
  }
  return out.id ? out : null;
}

/**
 * Store the snapshot. Called only with a `/api/me` projection — never with the login payload, which
 * is a different and smaller shape (see LOGIN-SEAM-1 in `useAuth`).
 */
export function writeUserSnapshot(user) {
  if (typeof window === "undefined") return;
  const snapshot = project(user);
  if (!snapshot) return;
  try {
    window.localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    // no-op: see the header. A faster first paint is never worth an exception.
  }
}

/**
 * The stored snapshot, or null. Projected again on read, so the allow-list holds even over bytes
 * this build did not write.
 */
export function readUserSnapshot() {
  if (typeof window === "undefined") return null;
  let raw = null;
  try {
    raw = window.localStorage.getItem(SNAPSHOT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    return project(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** Drop the snapshot. Every path where identity may have changed calls this. */
export function clearUserSnapshot() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(SNAPSHOT_KEY);
  } catch {
    // no-op
  }
}
